import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/api/client";
import { Search, Filter, User, Clock, Loader2 } from "lucide-react";

export default function PublicBrowse() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, facets: {} });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(params.get("q") || "");
  const selectedNiche = params.get("niche") || "";
  const selectedLanguage = params.get("language") || "";

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (query) p.set("q", query);
      if (selectedNiche) p.set("niche", selectedNiche);
      if (selectedLanguage) p.set("language", selectedLanguage);
      p.set("limit", "24");
      const { data } = await api.get(`/public/articles?${p.toString()}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [selectedNiche, selectedLanguage]); // eslint-disable-line

  const submit = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (query) next.set("q", query); else next.delete("q");
    setParams(next);
    load();
  };

  const setFacet = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-16 fade-up">
      <div className="border-b border-slate-800 pb-8 mb-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-3">— The Terminal · Public archive</p>
        <h1 className="font-serif text-5xl lg:text-6xl text-sky-100 tracking-tight">Browse the wire</h1>
        <p className="mt-3 text-neutral-400 max-w-2xl">Every publicly shared article written by our creators. Search, filter, and steal ideas.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <aside className="lg:col-span-1 space-y-6">
          <form onSubmit={submit} className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              data-testid="public-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles…"
              className="w-full bg-slate-900 border border-slate-800 rounded pl-9 pr-3 py-2.5 text-sky-50 focus:border-sky-400 outline-none text-sm"
            />
          </form>

          {data.facets?.niches?.length > 0 && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2 flex items-center gap-2"><Filter className="w-3 h-3" /> Niches</p>
              <div className="space-y-1">
                <button onClick={() => setFacet("niche", "")} className={`text-xs px-2.5 py-1 rounded font-mono block w-full text-left ${!selectedNiche ? "text-sky-300" : "text-neutral-500 hover:text-sky-200"}`}>All niches</button>
                {data.facets.niches.slice(0, 12).map((n) => (
                  <button
                    key={n}
                    data-testid={`facet-niche-${n.replace(/\s+/g,"-").toLowerCase()}`}
                    onClick={() => setFacet("niche", n)}
                    className={`text-xs px-2.5 py-1 rounded block w-full text-left truncate ${selectedNiche === n ? "bg-sky-400/10 text-sky-300 border border-sky-400/30" : "text-neutral-500 hover:text-sky-200"}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}

          {data.facets?.languages?.length > 0 && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Language</p>
              <div className="flex flex-wrap gap-1">
                <button onClick={() => setFacet("language", "")} className={`text-[11px] px-2 py-1 rounded font-mono ${!selectedLanguage ? "text-sky-300" : "text-neutral-500 hover:text-sky-200"}`}>Any</button>
                {data.facets.languages.map((l) => (
                  <button key={l} onClick={() => setFacet("language", l)} className={`text-[11px] px-2 py-1 rounded font-mono ${selectedLanguage === l ? "bg-sky-400/10 text-sky-300 border border-sky-400/30" : "text-neutral-500 hover:text-sky-200"}`}>{l}</button>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* Article grid */}
        <div className="lg:col-span-3">
          <p className="font-mono text-[11px] uppercase tracking-widest text-neutral-500 mb-4">
            {loading ? "Loading…" : `${data.total} article${data.total === 1 ? "" : "s"} · showing ${data.items.length}`}
          </p>
          {loading ? (
            <div className="text-center py-16 text-neutral-500 flex items-center justify-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading feed…
            </div>
          ) : data.items.length === 0 ? (
            <div className="border border-slate-800 rounded-xl p-16 text-center">
              <p className="font-serif text-xl text-sky-100">Nothing here yet.</p>
              <p className="text-neutral-500 text-sm mt-2">Be the first to publish. <Link to="/signup" className="text-sky-300 border-b border-sky-400/40">Create an account →</Link></p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {data.items.map((a) => (
                <Link
                  key={a.id}
                  data-testid={`article-card-${a.id}`}
                  to={`/read/${a.slug || a.id}`}
                  className="border border-slate-800 rounded-xl overflow-hidden hover:border-sky-400/40 transition group flex flex-col"
                >
                  {a.hero_image_url ? (
                    <img
                      src={a.hero_image_url.startsWith("http") ? a.hero_image_url : `${process.env.REACT_APP_BACKEND_URL}${a.hero_image_url}`}
                      alt={a.title}
                      className="w-full h-44 object-cover"
                    />
                  ) : (
                    <div className="w-full h-44 bg-gradient-to-br from-sky-500/20 to-slate-900" />
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400 mb-2 truncate">{a.niche}</p>
                    <h3 className="font-serif text-lg text-sky-100 group-hover:text-sky-300 transition line-clamp-2 leading-snug flex-1">{a.title}</h3>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800 text-[11px] font-mono text-neutral-500">
                      <span className="flex items-center gap-1 truncate">
                        <User className="w-3 h-3" /> {a.author_name}
                      </span>
                      <span className="flex items-center gap-2 flex-shrink-0">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {a.read_time || 3}m</span>
                        <span className="text-slate-700">·</span>
                        <span>{a.language || "EN"}</span>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
